import { Component, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { LanguageTranslation } from '../../../models/languages/language-translation.model';
import { LanguageTranslationsFiltersRequest } from '../../../models/languages/requests/api-languages-get-translations-request.model';
import { ApiLanguagesService } from '../../../services/languages/api/api-languages.service';
import { AlertsService } from '../../../services/alerts.service';
import { ModalsService } from '../../../services/modals.service';
import { LanguageTranslationAddModalComponent } from './modals/language-translation-add-modal/language-translation-add-modal.component';
import { LanguageTranslationEditModalComponent } from './modals/language-translation-edit-modal/language-translation-edit-modal.component';

@Component({
  templateUrl: 'language-translations.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, TooltipModule, CollapseModule,
    LanguageTranslationAddModalComponent, LanguageTranslationEditModalComponent]
})
export class LanguageTranslationsComponent implements OnInit {

  @ViewChild('addModal') addModal!: LanguageTranslationAddModalComponent;
  @ViewChild('editModal') editModal!: LanguageTranslationEditModalComponent;

  languageId: number = 0;
  isLoading: boolean = false;

  translations: LanguageTranslation[] = [];
  totalResults: number = 0;
  resultsPerPage: number = 25;
  pageNumber: number = 1;

  filters: LanguageTranslationsFiltersRequest = new LanguageTranslationsFiltersRequest();
  isFilterOpen: boolean = false;

  missingKeys: string[] = [];
  isMissingKeysOpen: boolean = false;
  isAddingAllKeys: boolean = false;

  get totalPages(): number {
    return Math.ceil(this.totalResults / this.resultsPerPage);
  }

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;
    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);
    const sorted = Array.from(visibleSet).sort((a, b) => a - b);
    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  }

  constructor(
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private apiLanguagesService: ApiLanguagesService,
    private alertsService: AlertsService,
    private modalsService: ModalsService
  ) {}

  highlightBrackets(value: string): SafeHtml {
    const html = value.replace(/\[([^\]]+)\]/g, '<span style="color:#f0ad4e;font-weight:bold">[$1]</span>');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const id: string = params['id'];
      if (id) {
        this.languageId = Number(id);
        this.refreshTranslations();
        this.refreshMissingKeys();
      }
    });
  }

  onClickFilter(): void {
    this.pageNumber = 1;
    this.refreshTranslations();
  }

  onClickResetFilters(): void {
    this.filters = new LanguageTranslationsFiltersRequest();
    this.pageNumber = 1;
    this.refreshTranslations();
  }

  onClickPaginationPrevious(): void {
    this.pageNumber--;
    this.refreshTranslations();
  }

  onClickPaginationNext(): void {
    this.pageNumber++;
    this.refreshTranslations();
  }

  goToPage(page: number): void {
    this.pageNumber = page;
    this.refreshTranslations();
  }

  onClickAdd(): void {
    this.addModal.show(() => {
      this.refreshTranslations();
      this.refreshMissingKeys();
    });
  }

  onClickEdit(translation: LanguageTranslation): void {
    this.editModal.show(translation.translationId, translation.translationKey, translation.translationValue, () => {
      this.refreshTranslations();
    });
  }

  onClickDelete(translation: LanguageTranslation): void {
    this.modalsService.showConfirmModal(() => {
      this.apiLanguagesService.deleteLanguageTranslation(this.languageId, translation.translationId).then(
        () => {
          this.alertsService.displayAlertSuccess('Translation deleted !');
          this.refreshTranslations();
          this.refreshMissingKeys();
        }, error => {
          this.alertsService.displayAlertError('Error deleting translation : ' + error);
        }
      );
    });
  }

  onClickAddAllKeys(): void {
    this.isAddingAllKeys = true;
    const promises = this.missingKeys.map(key =>
      this.apiLanguagesService.addLanguageTranslation(this.languageId, key, '')
    );
    Promise.all(promises).then(
      () => {
        this.isAddingAllKeys = false;
        this.alertsService.displayAlertSuccess('All missing keys added !');
        this.refreshTranslations();
        this.refreshMissingKeys();
      }, error => {
        this.isAddingAllKeys = false;
        this.alertsService.displayAlertError('Error adding keys : ' + error);
      }
    );
  }

  private refreshTranslations(): void {
    this.isLoading = true;
    this.apiLanguagesService.getLanguageTranslations(this.languageId, this.pageNumber, this.resultsPerPage, this.filters).then(
      response => {
        this.isLoading = false;
        this.translations = response.results;
        this.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading translations : ' + error);
      }
    );
  }

  private refreshMissingKeys(): void {
    this.apiLanguagesService.getLanguageTranslationsMissingKeys(this.languageId).then(
      response => {
        this.missingKeys = response.results;
      }, error => {
        this.alertsService.displayAlertError('Error loading missing keys : ' + error);
      }
    );
  }
}
